'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { getSupabaseBrowserClient } from '@/lib/supabase-browser';
import { Palette, Clock, Users, Square, RefreshCw, ZoomIn, ZoomOut, RotateCcw, Pipette } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { saveWeeklySnapshot, shouldTakeWeeklySnapshot, markWeeklySnapshotTaken } from '@/lib/canvas-snapshot';
import { useTranslations } from 'next-intl';

interface PixelCanvasProps {
  width?: number;
  height?: number;
  pixelSize?: number;
}

interface CanvasData {
  id: string;
  width: number;
  height: number;
  pixels: Array<{
    x: number;
    y: number;
    color: string;
    userId: string;
    username: string;
    timestamp: string;
  }>;
  lastUpdated: string;
  totalPixels: number;
  uniqueUsers: number;
  grid: string[][];
}

interface CanvasStats {
  totalPixels: number;
  uniqueUsers: number;
  lastUpdated: string;
  canvasSize: string;
}

const DEFAULT_COLORS = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
  '#FF00FF', '#00FFFF', '#FFA500', '#800080', '#FFC0CB', '#A52A2A',
  '#808080', '#000080', '#008000', '#800000', '#FFD700', '#C0C0C0'
];

export default function PixelCanvas({
  width = 200,
  height = 200,
  pixelSize = 7
}: PixelCanvasProps) {
  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const tPixel = useTranslations('pixelCanvas');
  const [canvasData, setCanvasData] = useState<CanvasData | null>(null);
  const [stats, setStats] = useState<CanvasStats | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<number>(0); // Real-time online users viewing the canvas
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [isPlacing, setIsPlacing] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState<number | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState<number | null>(null);
  const [countdownTimer, setCountdownTimer] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const recentlyPlacedPixelsRef = useRef<Set<string>>(new Set());
  const baseZoom = 1.5; // This is our "normal" size (0% zoom)
  
  // Initialize zoom level based on device type
  const getInitialZoom = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    return isMobile ? baseZoom * 0.7 : baseZoom * 0.5; // Start at minimum for each device
  };
  
  const [zoomLevel, setZoomLevel] = useState(getInitialZoom);
  
  // Get minimum zoom level based on device type
  const getMinZoom = () => {
    // Check if it's a mobile device
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    return isMobile ? baseZoom * 0.7 : baseZoom * 0.5; // 70% on mobile, 50% on desktop
  };

  // Get maximum zoom level based on device type
  const getMaxZoom = () => {
    // Check if it's a mobile device
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    return isMobile ? baseZoom * 6.67 : baseZoom * 3.33; // 1000% on mobile, 500% on desktop
  };
  
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [mouseDownPos, setMouseDownPos] = useState({ x: 0, y: 0 });
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isHoveringCanvas, setIsHoveringCanvas] = useState(false);
  const [isEditingColor, setIsEditingColor] = useState(false);
  const [tempColorCode, setTempColorCode] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isEyedropperMode, setIsEyedropperMode] = useState(false);
  
  // Touch handling for mobile
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
  const [lastTouchCenter, setLastTouchCenter] = useState<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const dropperInputRef = useRef<HTMLInputElement>(null);

  // Fetch canvas data (preserves optimistic updates)
  const fetchCanvasData = useCallback(async () => {
    try {
      const response = await fetch('/api/pixel-canvas');
      if (response.ok) {
        const data = await response.json();
        // Merge with existing canvas data to preserve optimistic updates
        setCanvasData(prev => {
          if (!prev) return data.canvas;
          
          // Create a copy of the server grid
          const mergedGrid = data.canvas.grid.map((row: string[]) => [...row]);
          
          // Preserve recently placed pixels from optimistic updates
          // This prevents pixels from being erased during auto-refresh
          prev.pixels.forEach(optimisticPixel => {
            const pixelKey = `${optimisticPixel.x},${optimisticPixel.y}`;
            if (recentlyPlacedPixelsRef.current.has(pixelKey)) {
              // Keep the optimistic pixel color
              if (optimisticPixel.y >= 0 && optimisticPixel.y < mergedGrid.length &&
                  optimisticPixel.x >= 0 && optimisticPixel.x < mergedGrid[0]?.length) {
                mergedGrid[optimisticPixel.y][optimisticPixel.x] = optimisticPixel.color;
              }
            }
          });
          
          // Merge pixels array, prioritizing optimistic updates
          const mergedPixels = [...data.canvas.pixels];
          prev.pixels.forEach(optimisticPixel => {
            const pixelKey = `${optimisticPixel.x},${optimisticPixel.y}`;
            if (recentlyPlacedPixelsRef.current.has(pixelKey)) {
              const existingIndex = mergedPixels.findIndex(
                p => p.x === optimisticPixel.x && p.y === optimisticPixel.y
              );
              if (existingIndex !== -1) {
                // Replace with optimistic version
                mergedPixels[existingIndex] = optimisticPixel;
              } else {
                // Add optimistic pixel if not in server data yet
                mergedPixels.push(optimisticPixel);
              }
            }
          });
          
          return {
            ...data.canvas,
            grid: mergedGrid,
            pixels: mergedPixels,
            totalPixels: mergedPixels.length,
            uniqueUsers: new Set(mergedPixels.map(p => p.userId)).size
          };
        });
        setStats(data.stats);
      }
    } catch (error) {
      // Silent error handling
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Real-time presence tracking for canvas viewers
  useEffect(() => {
    let channel: any;
    let active = true;
    
    (async () => {
      try {
        const supabaseClient = await getSupabaseBrowserClient();
        if (!active) return;
        
        // Clean up any existing channel
        const channelName = 'pixel-canvas-viewers';
        try {
          const existingChannel = supabaseClient.channel(channelName);
          await existingChannel.unsubscribe();
          await supabaseClient.removeChannel(existingChannel);
        } catch (e) {
          // Channel doesn't exist yet, that's fine
        }
        
        // Track presence for canvas viewers
        const presenceState = {
          userId: user?.id || 'anonymous',
          username: user?.username || 'Anonymous',
          joinedAt: new Date().toISOString()
        };
        
        const presenceKey = user?.id || `anon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        channel = supabaseClient
          .channel(channelName, {
            config: {
              broadcast: { self: false },
              presence: { key: presenceKey }
            }
          })
          .on('presence', { event: 'sync' }, () => {
            if (!active) return;
            const state = channel.presenceState();
            const onlineCount = Object.keys(state).filter(key => key !== 'anonymous').length;
            setOnlineUsers(onlineCount);
          })
          .on('presence', { event: 'join' }, () => {
            if (!active) return;
            const state = channel.presenceState();
            const onlineCount = Object.keys(state).filter(key => key !== 'anonymous').length;
            setOnlineUsers(onlineCount);
          })
          .on('presence', { event: 'leave' }, () => {
            if (!active) return;
            const state = channel.presenceState();
            const onlineCount = Object.keys(state).filter(key => key !== 'anonymous').length;
            setOnlineUsers(onlineCount);
          })
          .subscribe(async (status) => {
            if (active && status === 'SUBSCRIBED') {
              try {
                await channel.track(presenceState);
                
                // Get initial presence count
                setTimeout(() => {
                  if (!active) return;
                  const state = channel.presenceState();
                  const onlineCount = Object.keys(state).filter(key => key !== 'anonymous').length;
                  setOnlineUsers(onlineCount);
                }, 500);
              } catch (presenceError) {
                // Silent error handling
              }
            }
          });
      } catch (err) {
        // Silent error handling
      }
    })();
    
    return () => {
      active = false;
      if (channel) {
        (async () => {
          try {
            await channel.untrack();
            await channel.unsubscribe();
            const supabaseClient = await getSupabaseBrowserClient();
            await supabaseClient.removeChannel(channel);
            setOnlineUsers(0);
          } catch (err) {
            // Silent error handling
          }
        })();
      } else {
        setOnlineUsers(0);
      }
    };
  }, [user?.id]);

  // Real-time pixel placement updates
  useEffect(() => {
    let pixelChannel: any;
    let active = true;
    
    (async () => {
      try {
        const supabaseClient = await getSupabaseBrowserClient();
        if (!active) return;
        
        // Subscribe to pixel placement changes (both INSERT and UPDATE)
        // This ensures all users see pixel updates in real-time
        const handlePixelUpdate = (payload: any) => {
          if (!active) return;
          
          // Create a unique key for this pixel position
          const pixelKey = `${payload.new.x},${payload.new.y}`;
          
          // Skip real-time update if we just placed this pixel ourselves (optimistic update already handled it)
          // This prevents the blinking/flashing effect and discoloration
          if (recentlyPlacedPixelsRef.current.has(pixelKey)) {
            return;
          }
          
          // Update canvas data with the new pixel
          const newPixel = {
            x: payload.new.x,
            y: payload.new.y,
            color: payload.new.color,
            userId: payload.new.user_id,
            username: payload.new.username,
            timestamp: payload.new.placed_at || payload.new.updated_at || payload.new.created_at
          };
          
          // Update canvas data immediately for all users
          setCanvasData(prev => {
            if (!prev) return prev;
            
            // Check if pixel already exists at this position (update)
            const existingIndex = prev.pixels.findIndex(p => p.x === newPixel.x && p.y === newPixel.y);
            const isNewPixel = existingIndex === -1;
            let updatedPixels;
            
            if (existingIndex !== -1) {
              // Update existing pixel
              updatedPixels = [...prev.pixels];
              updatedPixels[existingIndex] = newPixel;
            } else {
              // Add new pixel
              updatedPixels = [...prev.pixels, newPixel];
            }
            
            // Update grid immediately
            const newGrid = prev.grid.map(row => [...row]);
            if (newPixel.y >= 0 && newPixel.y < newGrid.length && newPixel.x >= 0 && newPixel.x < newGrid[0].length) {
              newGrid[newPixel.y][newPixel.x] = newPixel.color;
            }
            
            // Update stats based on whether it's a new pixel
            setStats(prevStats => ({
              ...prevStats,
              totalPixels: isNewPixel ? (prevStats?.totalPixels || 0) + 1 : (prevStats?.totalPixels || 0)
            }));
            
            return {
              ...prev,
              pixels: updatedPixels,
              grid: newGrid,
              totalPixels: updatedPixels.length,
              uniqueUsers: new Set(updatedPixels.map(p => p.userId)).size
            };
          });
        };
        
        pixelChannel = supabaseClient
          .channel('pixel-placements-realtime')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'pixel_placements',
              filter: 'canvas_id=eq.main-canvas'
            },
            handlePixelUpdate
          )
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'pixel_placements',
              filter: 'canvas_id=eq.main-canvas'
            },
            handlePixelUpdate
          )
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              // Real-time subscription is active - all users will see updates
            } else if (status === 'CHANNEL_ERROR') {
              // Connection error - will retry on next mount
            }
          });
      } catch (err) {
        // Silent error handling - subscription will retry on next mount
      }
    })();
    
    return () => {
      active = false;
      if (pixelChannel) {
        (async () => {
          try {
            await pixelChannel.unsubscribe();
            const supabaseClient = await getSupabaseBrowserClient();
            await supabaseClient.removeChannel(pixelChannel);
          } catch (err) {
            // Silent error handling
          }
        })();
      }
    };
  }, []);

  // Check user cooldown
  const checkCooldown = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      const response = await fetch(`/api/pixel-canvas/cooldown?userId=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setCooldownRemaining(data.remainingMinutes);
        setCooldownSeconds(data.cooldownSeconds);
        
        // Start countdown timer if there's a cooldown (only if > 0)
        if (data.cooldownSeconds && data.cooldownSeconds > 0) {
          setCountdownTimer(data.cooldownSeconds);
        } else {
          // No cooldown - clear the timer
          setCountdownTimer(null);
        }
      }
    } catch (error) {
      console.error('Error checking cooldown:', error);
    }
  }, [isAuthenticated, user]);

  // Countdown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (countdownTimer && countdownTimer > 0) {
      interval = setInterval(() => {
        setCountdownTimer(prev => {
          if (prev && prev > 1) {
            return prev - 1;
          } else {
            // Timer finished, check cooldown again
            checkCooldown();
            return null;
          }
        });
      }, 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [countdownTimer, checkCooldown]);

  // Place a pixel
  const placePixel = async (x: number, y: number) => {
    if (!isAuthenticated || !user || isPlacing) {
      return;
    }

    // Optimistically update the canvas immediately (before API call)
    // This makes the pixel appear instantly
    setCanvasData(prev => {
      if (!prev) return prev;
      
      // Directly update the grid for fastest rendering
      const newGrid = prev.grid.map(row => [...row]);
      if (y >= 0 && y < newGrid.length && x >= 0 && x < newGrid[0].length) {
        newGrid[y][x] = selectedColor;
      }
      
      // Update pixels array
      const newPixel = {
        x,
        y,
        color: selectedColor,
        userId: user.id,
        username: user.username,
        timestamp: new Date().toISOString()
      };
      
      const existingIndex = prev.pixels.findIndex(p => p.x === x && p.y === y);
      const updatedPixels = existingIndex !== -1
        ? prev.pixels.map((p, i) => i === existingIndex ? newPixel : p)
        : [...prev.pixels, newPixel];
      
      // Mark this pixel as recently placed to prevent real-time subscription from overwriting
      const pixelKey = `${x},${y}`;
      recentlyPlacedPixelsRef.current.add(pixelKey);
      
      // Remove from set after a delay to allow future real-time updates
      setTimeout(() => {
        recentlyPlacedPixelsRef.current.delete(pixelKey);
      }, 3000);
      
      return {
        ...prev,
        pixels: updatedPixels,
        grid: newGrid,
        totalPixels: updatedPixels.length,
        uniqueUsers: new Set(updatedPixels.map(p => p.userId)).size
      };
    });

    // Fire API call in background - don't block UI
    // Only set isPlacing briefly to prevent rapid double-clicks
    setIsPlacing(true);
    setTimeout(() => setIsPlacing(false), 100);

    // Make API call without blocking
    fetch('/api/pixel-canvas/place', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        x,
        y,
        color: selectedColor,
        userId: user.id,
        username: user.username
      }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Success - optimistic update was correct, no need to do anything
          setCountdownTimer(null);
          checkCooldown();
        } else {
          // Only revert if we get a clear error response (not network errors)
          // Don't show toast for every error to avoid spam
          if (data.cooldownRemaining) {
            setCooldownRemaining(data.cooldownRemaining);
          }
        }
      })
      .catch(error => {
        // Network errors are silent - optimistic update stays
        // The real-time subscription will eventually sync if the pixel was actually placed
      });
  };

  // Handle pixel click
  const handlePixelClick = (x: number, y: number) => {
    if (!isAuthenticated || !user) {
      showToast(tPixel('pleaseSignInToPlacePixels'), 'error');
      return;
    }

    if (countdownTimer && countdownTimer > 0) {
      showToast(`Please wait ${countdownTimer} more second(s)`, 'error');
      return;
    }

    placePixel(x, y);
  };

  // Color editing functions
  const handleColorCodeEdit = () => {
    setIsEditingColor(true);
    setTempColorCode(selectedColor);
  };

  const handleColorCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Always ensure the value starts with #
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }
    
    setTempColorCode(value);
    
    // Update selected color in real-time if it's a valid hex code
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexPattern.test(value)) {
      setSelectedColor(value);
    }
  };

  const handleColorCodeBlur = () => {
    // Ensure the value has # prefix
    let finalValue = tempColorCode;
    if (finalValue && !finalValue.startsWith('#')) {
      finalValue = '#' + finalValue;
    }
    
    // Validate and finalize the color code when user clicks away
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexPattern.test(finalValue)) {
      setSelectedColor(finalValue);
      setTempColorCode(finalValue);
    } else {
      // Reset to last valid color if invalid
      setTempColorCode(selectedColor);
    }
    setIsEditingColor(false);
  };

  const handleColorCodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      handleColorCodeBlur();
    }
  };

  // Color picker functionality
  const handleColorBarClick = () => {
    setShowColorPicker(!showColorPicker);
  };

  const handleDropperClick = async () => {
    if (isEyedropperMode) {
      // Deactivate eyedropper mode
      setIsEyedropperMode(false);
      return;
    }

    try {
      // Check if Eyedropper API is supported
      if ('EyeDropper' in window) {
        // Use native Eyedropper API (Chrome 95+)
        const eyeDropper = new (window as any).EyeDropper();
        const result = await eyeDropper.open();
        
        if (result && result.sRGBHex) {
          setSelectedColor(result.sRGBHex);
          setTempColorCode(result.sRGBHex);
          showToast(tPixel('colorPickedFromScreen'), 'success');
        }
      } else {
        // Fallback to screen capture for browsers without Eyedropper API
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true
        });
        
        // Create a hidden video element to capture the screen
        const video = document.createElement('video');
        video.srcObject = stream;
        video.style.position = 'fixed';
        video.style.top = '0';
        video.style.left = '0';
        video.style.width = '100vw';
        video.style.height = '100vh';
        video.style.zIndex = '9999';
        video.style.pointerEvents = 'none';
        video.style.opacity = '0';
        video.style.visibility = 'hidden';
        document.body.appendChild(video);
        
        video.play();
        
        // Wait for video to be ready
        video.addEventListener('loadedmetadata', () => {
          // Create overlay for color picking
          const overlay = document.createElement('div');
          overlay.style.position = 'fixed';
          overlay.style.top = '0';
          overlay.style.left = '0';
          overlay.style.width = '100vw';
          overlay.style.height = '100vh';
          overlay.style.zIndex = '10000';
          overlay.style.cursor = 'crosshair';
          overlay.style.backgroundColor = 'rgba(0,0,0,0.05)';
          document.body.appendChild(overlay);
          
          // Create magnifier
          const magnifier = document.createElement('canvas');
          magnifier.width = 120;
          magnifier.height = 120;
          magnifier.style.position = 'fixed';
          magnifier.style.zIndex = '10001';
          magnifier.style.border = '3px solid #333';
          magnifier.style.borderRadius = '8px';
          magnifier.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
          magnifier.style.pointerEvents = 'none';
          document.body.appendChild(magnifier);
          
          const magnifierCtx = magnifier.getContext('2d');
          
          // Handle mouse move for magnifier
          const handleMouseMove = (e: MouseEvent) => {
            if (magnifierCtx) {
              // Position magnifier centered on cursor
              magnifier.style.left = `${e.clientX - 60}px`;
              magnifier.style.top = `${e.clientY - 60}px`;
              
              // Draw magnified area (4x zoom)
              magnifierCtx.clearRect(0, 0, 120, 120);
              magnifierCtx.imageSmoothingEnabled = false;
              magnifierCtx.drawImage(
                video,
                e.clientX - 15, e.clientY - 15, 30, 30,
                0, 0, 120, 120
              );
              
              // Draw crosshair at center
              magnifierCtx.strokeStyle = '#ff0000';
              magnifierCtx.lineWidth = 2;
              magnifierCtx.beginPath();
              magnifierCtx.moveTo(60, 50);
              magnifierCtx.lineTo(60, 70);
              magnifierCtx.moveTo(50, 60);
              magnifierCtx.lineTo(70, 60);
              magnifierCtx.stroke();
            }
          };
          
          // Handle click to pick color
          const handleClick = (e: MouseEvent) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {
              canvas.width = 1;
              canvas.height = 1;
              ctx.drawImage(video, e.clientX, e.clientY, 1, 1, 0, 0, 1, 1);
              const imageData = ctx.getImageData(0, 0, 1, 1);
              const data = imageData.data;
              const r = data[0];
              const g = data[1];
              const b = data[2];
              const color = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
              
              setSelectedColor(color);
              setTempColorCode(color);
              showToast(tPixel('colorPickedFromScreen'), 'success');
            }
            
            // Clean up
            stream.getTracks().forEach(track => track.stop());
            document.body.removeChild(video);
            document.body.removeChild(overlay);
            document.body.removeChild(magnifier);
            overlay.removeEventListener('mousemove', handleMouseMove);
            overlay.removeEventListener('click', handleClick);
          };
          
          overlay.addEventListener('mousemove', handleMouseMove);
          overlay.addEventListener('click', handleClick);
          
          // Handle escape key to cancel
          const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
              stream.getTracks().forEach(track => track.stop());
              document.body.removeChild(video);
              document.body.removeChild(overlay);
              document.body.removeChild(magnifier);
              overlay.removeEventListener('mousemove', handleMouseMove);
              overlay.removeEventListener('click', handleClick);
              document.removeEventListener('keydown', handleKeyDown);
            }
          };
          
          document.addEventListener('keydown', handleKeyDown);
        });
      }
      
    } catch (error) {
      console.error('Error with eyedropper:', error);
      showToast('Eyedropper not supported or denied', 'error');
    }
  };

  const getColorFromPixel = (x: number, y: number): string => {
    if (!canvasRef.current) return selectedColor;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return selectedColor;
    
    // Get the pixel data
    const imageData = ctx.getImageData(x, y, 1, 1);
    const data = imageData.data;
    const r = data[0];
    const g = data[1];
    const b = data[2];
    const a = data[3];
    
    // Convert to hex
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };


  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    setTempColorCode(color);
  };

  // Zoom controls (relative to base zoom)
  const handleZoomIn = () => {
    const newZoomLevel = Math.min(zoomLevel + 0.5, getMaxZoom());
    setZoomLevel(newZoomLevel);
  };

  const handleZoomOut = () => {
    const newZoomLevel = Math.max(zoomLevel - 0.5, getMinZoom());
    setZoomLevel(newZoomLevel);
  };

  // Helper function to center canvas at a specific zoom level
  const centerCanvas = (zoomLevel: number) => {
    if (canvasData && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width - 32;
      const containerHeight = containerRect.height - 32;
      
      const canvasWidth = canvasData.width * pixelSize * zoomLevel;
      const canvasHeight = canvasData.height * pixelSize * zoomLevel;
      
      // Center the canvas in the container
      const centerX = (containerWidth - canvasWidth) / 2;
      const centerY = (containerHeight - canvasHeight) / 2;
      
      // Apply constraints but don't force centering if it would go out of bounds
      const constrained = constrainPan(centerX, centerY, zoomLevel);
      setPanX(constrained.x);
      setPanY(constrained.y);
    }
  };

  const handleResetView = () => {
    setZoomLevel(getMinZoom()); // Reset to minimum zoom (80% on mobile, 50% on desktop)
    // Reset pan to center (canvas is now centered by CSS)
    setPanX(0);
    setPanY(0);
  };

  // Helper function to constrain pan values - removed constraints for free panning
  const constrainPan = (newPanX: number, newPanY: number, currentZoomLevel = zoomLevel) => {
    // No constraints - allow free panning in all directions
    // Users can pan anywhere to see the entire canvas when zoomed in
    return { x: newPanX, y: newPanY };
  };



  // Pan controls - simplified like WeeklyCanvasSnapshot
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) { // Left click
      e.preventDefault();
      setIsMouseDown(true);
      setMouseDownPos({ x: e.clientX, y: e.clientY });
      setIsDragging(false); // Start as false, only set to true if mouse moves
      setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
    }
  };
    
  const handleMouseMove = (e: React.MouseEvent) => {
    // Only handle dragging if mouse button is actually down
    if (!isMouseDown) {
      return;
    }
    
    // Check if mouse moved significantly (more than 5 pixels) to distinguish drag from click
    const moveDistance = Math.sqrt(
      Math.pow(e.clientX - mouseDownPos.x, 2) + Math.pow(e.clientY - mouseDownPos.y, 2)
    );
    
    const hasDragged = moveDistance > 5;
    
    if (hasDragged) {
      setIsDragging(true);
      e.preventDefault();
      const newPanX = e.clientX - dragStart.x;
      const newPanY = e.clientY - dragStart.y;
      const constrained = constrainPan(newPanX, newPanY);
      
      setPanX(constrained.x);
      setPanY(constrained.y);
    } else if (isDragging) {
      // Continue dragging if already dragging
      e.preventDefault();
      const newPanX = e.clientX - dragStart.x;
      const newPanY = e.clientY - dragStart.y;
      const constrained = constrainPan(newPanX, newPanY);
      
      setPanX(constrained.x);
      setPanY(constrained.y);
    }
  };

  // Touch event handlers for mobile
  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return null;
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) + 
      Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  };

  const getTouchCenter = (touches: React.TouchList) => {
    if (touches.length === 0) return null;
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    const touch1 = touches[0];
    const touch2 = touches[1];
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // e.preventDefault(); // Removed to avoid passive event listener error
    
    if (e.touches.length === 1) {
      // Single touch - prepare for drag or click
      const touch = e.touches[0];
      setMouseDownPos({ x: touch.clientX, y: touch.clientY });
      setIsDragging(false); // Start as false, only set to true if finger moves
      setDragStart({ x: touch.clientX - panX, y: touch.clientY - panY });
      setIsMouseDown(true);
    } else if (e.touches.length === 2) {
      // Two touches - start pinch zoom
      const distance = getTouchDistance(e.touches);
      const center = getTouchCenter(e.touches);
      if (distance && center) {
        setLastTouchDistance(distance);
        setLastTouchCenter(center);
      }
      setIsMouseDown(false); // Cancel click if two fingers
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // e.preventDefault(); // Removed to avoid passive event listener error
    
    if (e.touches.length === 1 && isMouseDown) {
      // Check if finger moved significantly (more than 5 pixels) to distinguish drag from tap
      const touch = e.touches[0];
      const moveDistance = Math.sqrt(
        Math.pow(touch.clientX - mouseDownPos.x, 2) + Math.pow(touch.clientY - mouseDownPos.y, 2)
      );
      
      const hasDragged = moveDistance > 5;
      
      if (hasDragged) {
        setIsDragging(true);
        const newPanX = touch.clientX - dragStart.x;
        const newPanY = touch.clientY - dragStart.y;
        const constrained = constrainPan(newPanX, newPanY);
        
        setPanX(constrained.x);
        setPanY(constrained.y);
      } else if (isDragging) {
        // Continue dragging if already dragging
        const newPanX = touch.clientX - dragStart.x;
        const newPanY = touch.clientY - dragStart.y;
        const constrained = constrainPan(newPanX, newPanY);
        
        setPanX(constrained.x);
        setPanY(constrained.y);
      }
    } else if (e.touches.length === 2 && lastTouchDistance && lastTouchCenter) {
      // Two finger pinch zoom
      const currentDistance = getTouchDistance(e.touches);
      const currentCenter = getTouchCenter(e.touches);
      
      if (currentDistance && currentCenter) {
        const scale = currentDistance / lastTouchDistance;
        const delta = scale > 1 ? 0.1 : -0.1;
        const newZoomLevel = Math.max(getMinZoom(), Math.min(getMaxZoom(), zoomLevel + delta));
        
        if (newZoomLevel !== zoomLevel && canvasData && containerRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const centerX = currentCenter.x - containerRect.left;
          const centerY = currentCenter.y - containerRect.top;
          
          const containerCenterX = containerRect.width / 2;
          const containerCenterY = containerRect.height / 2;
          
          const canvasX = (centerX - containerCenterX - panX) / (pixelSize * zoomLevel);
          const canvasY = (centerY - containerCenterY - panY) / (pixelSize * zoomLevel);
          
          setZoomLevel(newZoomLevel);
          
          const newPanX = centerX - containerCenterX - (canvasX * pixelSize * newZoomLevel);
          const newPanY = centerY - containerCenterY - (canvasY * pixelSize * newZoomLevel);
          
          const constrained = constrainPan(newPanX, newPanY, newZoomLevel);
          setPanX(constrained.x);
          setPanY(constrained.y);
        }
        
        setLastTouchDistance(currentDistance);
        setLastTouchCenter(currentCenter);
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    
    if (e.touches.length === 0) {
      // All touches ended
      const wasDragging = isDragging;
      setIsDragging(false);
      setLastTouchDistance(null);
      setLastTouchCenter(null);
      
      // Handle pixel placement on tap (if single touch and no drag)
      if (!wasDragging && isMouseDown && e.changedTouches.length === 1 && canvasData && containerRef.current) {
        const touch = e.changedTouches[0];
        const moveDistance = Math.sqrt(
          Math.pow(touch.clientX - mouseDownPos.x, 2) + Math.pow(touch.clientY - mouseDownPos.y, 2)
        );
        
        if (moveDistance <= 5) {
          // Handle pixel placement on tap
          const canvas = canvasRef.current;
          const canvasRect = canvas.getBoundingClientRect();
          
          // Get touch position relative to the canvas element (getBoundingClientRect accounts for transform)
          // But we need to account for the canvas border (1px on each side)
          const borderWidth = 1; // border-gray-300 is 1px
          const touchX = touch.clientX - canvasRect.left - borderWidth;
          const touchY = touch.clientY - canvasRect.top - borderWidth;
          
          // Canvas actual size (accounting for zoom)
          const scaledCanvasWidth = canvasRect.width - (borderWidth * 2);
          const scaledCanvasHeight = canvasRect.height - (borderWidth * 2);
          
          // Convert to canvas internal coordinates (canvas.width/height are the actual pixel dimensions)
          // The canvas is scaled, so we need to scale the coordinates back
          const scaleX = canvas.width / scaledCanvasWidth;
          const scaleY = canvas.height / scaledCanvasHeight;
          
          const x = Math.floor(touchX * scaleX / pixelSize);
          const y = Math.floor(touchY * scaleY / pixelSize);
          
          if (x >= 0 && x < canvasData.width && y >= 0 && y < canvasData.height) {
            handlePixelClick(x, y);
          }
        }
      }
      
      setIsMouseDown(false);
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isMouseDown) {
      return;
    }
    
    const wasDragging = isDragging;
    setIsMouseDown(false);
    
    // Check if this was a click (no significant movement) vs a drag
    const moveDistance = Math.sqrt(
      Math.pow(e.clientX - mouseDownPos.x, 2) + Math.pow(e.clientY - mouseDownPos.y, 2)
    );
    
    if (!wasDragging && moveDistance <= 5 && canvasData && canvasRef.current && containerRef.current) {
      // Handle pixel placement on click
      const canvas = canvasRef.current;
      const canvasRect = canvas.getBoundingClientRect();
      
      // Get mouse position relative to the canvas element (getBoundingClientRect accounts for transform)
      // But we need to account for the canvas border (1px on each side)
      const borderWidth = 1; // border-gray-300 is 1px
      const mouseX = e.clientX - canvasRect.left - borderWidth;
      const mouseY = e.clientY - canvasRect.top - borderWidth;
      
      // Canvas actual size (accounting for zoom)
      const scaledCanvasWidth = canvasRect.width - (borderWidth * 2);
      const scaledCanvasHeight = canvasRect.height - (borderWidth * 2);
      
      // Convert to canvas internal coordinates (canvas.width/height are the actual pixel dimensions)
      // The canvas is scaled, so we need to scale the coordinates back
      const scaleX = canvas.width / scaledCanvasWidth;
      const scaleY = canvas.height / scaledCanvasHeight;
      
      const x = Math.floor(mouseX * scaleX / pixelSize);
      const y = Math.floor(mouseY * scaleY / pixelSize);
      
      if (x >= 0 && x < canvasData.width && y >= 0 && y < canvasData.height) {
        handlePixelClick(x, y);
      }
    }
    setIsDragging(false);
  };

  // Draw canvas
  const drawCanvas = useCallback(() => {
    if (!canvasRef.current || !canvasData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvasData.width * pixelSize;
    canvas.height = canvasData.height * pixelSize;

    // Enable crisp pixel rendering
    ctx.imageSmoothingEnabled = false;
    (ctx as any).webkitImageSmoothingEnabled = false;
    (ctx as any).mozImageSmoothingEnabled = false;
    (ctx as any).msImageSmoothingEnabled = false;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw pixels first
    canvasData.grid.forEach((row, y) => {
      row.forEach((color, x) => {
        if (color && color !== '') {
          ctx.fillStyle = color;
          ctx.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize);
        }
      });
    });

    // Draw crisp grid lines
    ctx.strokeStyle = 'rgba(156, 163, 175, 0.4)'; // More subtle gray
    ctx.lineWidth = 0.5;
    
    // Draw vertical lines
    for (let x = 0; x <= canvasData.width; x++) {
      ctx.beginPath();
      ctx.moveTo(x * pixelSize, 0);
      ctx.lineTo(x * pixelSize, canvas.height);
      ctx.stroke();
    }
    
    // Draw horizontal lines
    for (let y = 0; y <= canvasData.height; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * pixelSize);
      ctx.lineTo(canvas.width, y * pixelSize);
      ctx.stroke();
    }
  }, [canvasData, pixelSize]);



  // Mouse enter/leave handlers for canvas container
  const handleMouseEnter = () => {
    setIsHoveringCanvas(true);
  };

  const handleMouseLeave = () => {
    setIsHoveringCanvas(false);
  };

  // Update cooldown timer
  useEffect(() => {
    if (cooldownRemaining && cooldownRemaining > 0) {
      const timer = setTimeout(() => {
        setCooldownRemaining(prev => prev ? prev - 1 : null);
      }, 60000);

      return () => clearTimeout(timer);
    } else if (cooldownRemaining === 0) {
      setCooldownRemaining(null);
      checkCooldown();
    }
  }, [cooldownRemaining, checkCooldown]);

  // Auto-refresh canvas data every 30 seconds (realtime handles most updates)
  useEffect(() => {
    fetchCanvasData();
    checkCooldown();

    const interval = setInterval(() => {
      fetchCanvasData();
    }, 30000); // 30 seconds - real-time handles most updates now

    return () => clearInterval(interval);
  }, [fetchCanvasData, checkCooldown]);

  // Redraw canvas when data changes
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Take weekly snapshot if needed
  const takeWeeklySnapshot = useCallback(async () => {
    if (shouldTakeWeeklySnapshot() && canvasRef.current && canvasData) {
      try {
        const success = await saveWeeklySnapshot(canvasRef.current, canvasData);
        if (success) {
          markWeeklySnapshotTaken();
          // Silent success for weekly snapshot
        }
      } catch (error) {
        console.error('Error taking weekly snapshot:', error);
      }
    }
  }, [canvasData]);

  // Check for weekly snapshot when canvas data changes
  useEffect(() => {
    if (canvasData) {
      takeWeeklySnapshot();
    }
  }, [canvasData, takeWeeklySnapshot]);

  // Initialize canvas when it loads
  useEffect(() => {
    if (canvasData && containerRef.current && !isInitialized) {
      // Canvas is now centered by CSS, just initialize pan to center
      setPanX(0);
      setPanY(0);
      setIsInitialized(true);
    }
  }, [canvasData, isInitialized]);

  // Document-level mouseup handler to reset mouse state if released outside canvas
  useEffect(() => {
    const handleDocumentMouseUp = () => {
      setIsMouseDown(false);
      setIsDragging(false);
    };

    document.addEventListener('mouseup', handleDocumentMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, []);

  // Document-level wheel listener to properly prevent default
  useEffect(() => {
    const handleDocumentWheel = (e: WheelEvent) => {
      if (isHoveringCanvas) {
        e.preventDefault();
        e.stopPropagation();
        
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const newZoomLevel = Math.max(getMinZoom(), Math.min(getMaxZoom(), zoomLevel + delta));
        
        if (newZoomLevel !== zoomLevel && canvasData && containerRef.current) {
          // Get mouse position relative to the canvas container
          const containerRect = containerRef.current.getBoundingClientRect();
          const mouseX = e.clientX - containerRect.left;
          const mouseY = e.clientY - containerRect.top;
          
          // Get container center (since canvas is centered with flexbox)
          const containerCenterX = containerRect.width / 2;
          const containerCenterY = containerRect.height / 2;
          
          // Calculate the point on the canvas that the mouse is hovering over
          // Account for the canvas being centered in the container
          const canvasX = (mouseX - containerCenterX - panX) / (pixelSize * zoomLevel);
          const canvasY = (mouseY - containerCenterY - panY) / (pixelSize * zoomLevel);
          
          // Update zoom level
          setZoomLevel(newZoomLevel);
          
          // Adjust pan to keep the same point under the mouse
          const newPanX = mouseX - containerCenterX - (canvasX * pixelSize * newZoomLevel);
          const newPanY = mouseY - containerCenterY - (canvasY * pixelSize * newZoomLevel);
          
          // Apply constraints
          const constrained = constrainPan(newPanX, newPanY, newZoomLevel);
          setPanX(constrained.x);
          setPanY(constrained.y);
        }
      }
    };

    document.addEventListener('wheel', handleDocumentWheel, { passive: false });
    
    return () => {
      document.removeEventListener('wheel', handleDocumentWheel);
    };
  }, [isHoveringCanvas, zoomLevel, panX, panY]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">{tPixel('loadingCanvas')}</span>
      </div>
    );
  }

  if (!canvasData) {
    return (
      <div className="text-center p-8">
        <p className="text-red-500">{tPixel('failedToLoadCanvas')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-none shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="text-center mb-6">
        {/* Title */}
        <div className="flex items-center justify-center space-x-2 mb-3">
          <Square className="w-6 h-6 text-blue-500" />
          <h2 className="text-xl font-bold text-gray-900">{tPixel('communityPixelCanvas')}</h2>
        </div>
        
        {/* Stats - Second Line */}
        {stats && (
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600 flex-wrap gap-2">
            <div className="flex items-center space-x-1">
              <Square className="w-4 h-4" />
              <span>{width * height} {tPixel('pixels')}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Users className="w-4 h-4" />
              <span>{onlineUsers > 0 ? onlineUsers : (stats?.uniqueUsers || 0)} {tPixel('users')}</span>
            </div>
          </div>
        )}
      </div>


      {/* Cooldown Status - DISABLED FOR TESTING */}
      {false && isAuthenticated && cooldownRemaining && (
        <div className="mb-4 p-3 bg-yellow-100 text-yellow-800 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>{tPixel('cooldownMinutesRemaining', { minutes: cooldownRemaining })}</span>
          </div>
        </div>
      )}

              <div className="flex flex-col xl:flex-row gap-6">
          {/* Canvas */}
          <div className="flex-1 min-w-0">
          {/* Zoom Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleZoomOut}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                title={tPixel('zoomOut')}
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 min-w-[60px] text-center">
                {Math.round((zoomLevel / baseZoom) * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                title={tPixel('zoomIn')}
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                onClick={handleResetView}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                title={tPixel('resetView')}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
            
            {/* Cooldown Status */}
            {isAuthenticated && (
              <div className="flex items-center space-x-1 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>
                  {countdownTimer ? (
                    tPixel('cooldownSeconds', { seconds: countdownTimer })
                  ) : tPixel('readyToPaint')}
                </span>
              </div>
            )}
          </div>

          {/* Canvas Container */}
          <div 
            ref={containerRef}
            className="bg-gray-100 p-4 rounded-lg overflow-hidden flex items-center justify-center h-[400px] sm:h-[700px]"
            style={{ 
              width: '100%', 
              position: 'relative',
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none', // Prevent default touch behaviors
              userSelect: 'none', // Prevent text selection
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="inline-block origin-center"
              style={{
                transform: `translate(${panX}px, ${panY}px) scale(${zoomLevel})`,
                transformOrigin: 'center center'
              }}
            >
              <canvas
                ref={canvasRef}
                className={`border border-gray-300 ${isEyedropperMode ? 'cursor-crosshair' : 'cursor-pointer'}`}
                style={{
                  imageRendering: 'pixelated',
                  maxWidth: '100%',
                  height: 'auto',
                  cursor: isEyedropperMode ? 'crosshair' : 'pointer',
                  touchAction: 'none', // Prevent default touch behaviors
                  userSelect: 'none', // Prevent text selection
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none'
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              />
            </div>
          </div>
        </div>


        {/* Color Picker */}
        <div className="xl:w-80">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Palette className="w-5 h-5 mr-2 text-blue-500" />
              {tPixel('colorPalette')}
            </h3>
            
            {/* Color Grid */}
            <div className="grid grid-cols-6 gap-3 mb-6">
              {DEFAULT_COLORS.map((color) => (
                <button
                  key={color}
                  className={`w-10 h-10 rounded-xl border-2 transition-all duration-200 hover:scale-110 hover:shadow-md ${
                    selectedColor === color 
                      ? 'border-blue-500 shadow-lg ring-2 ring-blue-200' 
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    setSelectedColor(color);
                    setTempColorCode(color);
                  }}
                  title={color}
                />
              ))}
            </div>
            
            {/* Color Picker */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-700">
                  {tPixel('colorPicker')}
                </label>
                <div className="relative">
                  <button
                    data-dropper-button
                    onClick={handleDropperClick}
                    className={`px-3 py-2 rounded-xl border-2 transition-colors flex items-center justify-center ${
                      isEyedropperMode 
                        ? 'border-blue-500 bg-blue-50 text-blue-600' 
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                    title={isEyedropperMode ? tPixel('deactivateEyedropper') : tPixel('pickColorFromScreen')}
                  >
                    <Pipette className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Clickable color bar that opens React Colorful */}
              <div
                onClick={handleColorBarClick}
                className="w-full h-12 rounded-xl border-2 border-gray-200 cursor-pointer hover:border-gray-300 transition-colors flex items-center justify-center mb-4"
                style={{ backgroundColor: selectedColor }}
                title={tPixel('openAdvancedColorPicker')}
              >
                <span className="text-white font-semibold text-sm drop-shadow-lg">
                  {selectedColor}
                </span>
              </div>
              
              {showColorPicker && (
                <div className="mb-4 p-4 bg-white rounded-xl border-2 border-gray-200">
                  <HexColorPicker
                    color={selectedColor}
                    onChange={handleColorChange}
                    style={{ width: '100%' }}
                  />
                </div>
              )}

              {isEyedropperMode && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700 font-medium">
                    {tPixel('eyedropperActive')}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {tPixel('eyedropperInstructions')}
                  </p>
                </div>
              )}
              
            </div>
            
            {/* Selected Color Display */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center space-x-4">
                <div
                  className="w-16 h-16 rounded-xl border-2 border-gray-300 shadow-sm"
                  style={{ backgroundColor: selectedColor }}
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900 mb-1">{tPixel('selectedColor')}</p>
                  {isEditingColor ? (
                    <input
                      type="text"
                      value={tempColorCode}
                      onChange={handleColorCodeChange}
                      onBlur={handleColorCodeBlur}
                      onKeyDown={handleColorCodeKeyPress}
                      className="text-xs font-mono bg-white px-2 py-1 rounded border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none w-full"
                      placeholder="000000"
                      autoFocus
                    />
                  ) : (
                    <div 
                      className="text-xs text-gray-600 font-mono bg-white px-2 py-1 rounded border cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={handleColorCodeEdit}
                      title={tPixel('clickToEditColorCode')}
                    >
                      {selectedColor}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Hint Section */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">{tPixel('hints')}</p>
                              <ul className="space-y-1">
                  <li>• {tPixel('singleClickHint')}</li>
                  <li>• {tPixel('dragHint')}</li>
                  <li className="bg-yellow-100 border border-yellow-300 rounded-lg p-2 mt-2">
                    <strong>{tPixel('chatHint')}</strong>
                  </li>
                </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}